import React from 'react';
import { Subtitle } from '../Common/Subtitle';
import { Box } from '../Common/Box';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { LabeledValue } from '../Common/LabeledValue';
import cx from 'classnames';
import { CreditCard } from '~/types/models';
import useCurrentUser from '~/react-hooks/use-current-user';
import route from 'ziggy-js';
import { Tooltip } from '@vimily/bonjoro-ui-components-v2/Tooltip';

export interface CreditCardPanelProps {
  creditCard?: CreditCard;
  className?: string;
  boxClassName?: string;
  detailContainerClassName?: string;
  buttonContainerClassName?: string;
  hasSavedCreditCard?: boolean;
  onClick?: () => void;
}

export function CreditCardPanel({
  creditCard,
  className,
  boxClassName,
  detailContainerClassName,
  buttonContainerClassName,
  hasSavedCreditCard,
  onClick,
}: CreditCardPanelProps) {
  const { is_email_verified } = useCurrentUser();

  const emailVerificationNeeded = !is_email_verified && hasSavedCreditCard;

  return (
    <div
      className={cx(className, 'flex flex-col gap-y-4 w-full max-w-[800px]')}
    >
      <Subtitle label='Credit card' />
      <Box
        className={cx(
          boxClassName,
          'flex flex-row gap-y-4 items-start justify-between gap-x-4'
        )}
      >
        <div
          className={cx(
            detailContainerClassName,
            'flex flex-row gap-x-6 gap-y-4'
          )}
        >
          {creditCard ? (
            <i className={`fab fa-cc-${creditCard.brand} text-[40px]`} />
          ) : (
            <i className='icon icon-credit-card !h-11 !w-11 !v2-bg-neutral-300' />
          )}
          <LabeledValue
            label='Card number'
            value={
              creditCard ? `XXXX XXXX XXXX ${creditCard.last4}` : undefined
            }
          />
          <LabeledValue
            label='Expiry date'
            value={
              creditCard
                ? `${creditCard.exp_month}/${creditCard.exp_year}`
                : undefined
            }
          />
        </div>
        <div className={cx(buttonContainerClassName, 'flex')}>
          <Tooltip
            disabled={!emailVerificationNeeded}
            childrenContainerClassName={buttonContainerClassName}
            content={<span>Please verify your email address</span>}
          >
            <Button
              variant={ButtonVariant.Secondary}
              className={cx('w-full v2-text-neutral-900 py-2', {
                '!v2-border-neutral-100 !v2-text-neutral-300 !v2-bg-white':
                  emailVerificationNeeded,
              })}
              onClick={onClick}
              disabled={emailVerificationNeeded}
            >
              {creditCard ? 'Update card' : 'Add card'}
            </Button>
          </Tooltip>
        </div>
      </Box>
      {emailVerificationNeeded && (
        <div
          className={cx(
            'v2-bg-neutral-50 rounded-md w-full items-center ',
            'flex flex-row gap-x-[15px] py-[15px] px-5 h-[50px]',
            'v2-animate-fade-in-150'
          )}
        >
          <i className='icon icon-info' />
          <span className='text-base v2-text-neutral-900'>
            Please&nbsp;
            <a
              href={route('email::verification.notice')}
              target='_blank'
              rel='noopener noreferrer'
              className='v2-text-neutral-500 hover:v2-text-neutral-900 underline hover:underline active:v2-text-neutral-500 focus:!v2-text-neutral-500 focus:!underline'
            >
              verify your email
            </a>
            &nbsp;to update your card details
          </span>
        </div>
      )}
    </div>
  );
}
